import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LeftSidebar from "./LeftSidebar";
import { useSearchParams } from "react-router-dom";
import { setBreadCrumbData } from "@Reducers/breadcrumbsdata";
import { getProjectDashboardPageUrl } from "@Features/projects/utils/projectUrls";
import { setShowStickySidebar, setSidebarSelectedCell } from "@Reducers/stickySidebar";
import { useTranslation } from "@i18n";
import _ from "lodash";
import PermissionGate from "@Pages/Error/PermissionGate";
import { RootState } from "@Reducers/index";
import {
  MODULE_TAB_TYPE,
  MODULE_SETTING_TYPE,
} from "@Features/portal-settings/components/PortalSetings/Configuration/Modules/constants";

const SETTINGS_COMPONENTS: any = {
  resource_type: lazy(() => import("@Components/Resource/ResourceType")),
  currency: lazy(() =>
    import("@Features/projects").then((module: any) => ({ default: module.ProjectCurrencySetting })),
  ),
  spaces: lazy(() => import("@Features/projects").then((module: any) => ({ default: module.ProjectSpacesList }))),
  roles: lazy(() => import("@Features/projects").then((module: any) => ({ default: module.ProjectRolesList }))),
  users: lazy(() => import("@Features/projects").then((module: any) => ({ default: module.ProjectUsersList }))),
  groups: lazy(() => import("@Features/projects").then((module: any) => ({ default: module.ProjectGroupsList }))),
  teams: lazy(() => import("@Features/projects").then((module: any) => ({ default: module.ProjectTeams }))),
  inspection_location: lazy(() =>
    import("@Features/projects").then((module: any) => ({ default: module.ProjectInspectionLocation })),
  ),
  WBS: lazy(() => import("@Features/projects").then((module: any) => ({ default: module.ProjectWBS }))),
  general_information: lazy(() => import("./GeneralProjectInformation")),
  formTemplates: lazy(() =>
    import("@Features/projects").then((module: any) => ({ default: module.ProjectFormTemplates })),
  ),
  space: lazy(() => import("@Features/projects").then((module: any) => ({ default: module.spaceDetails }))),
  assetTypes: lazy(() => import("@Features/projects").then((module: any) => ({ default: module.assetTypes }))),
  userSpaces: lazy(() => import("@Features/projects").then((module: any) => ({ default: module.ProjectUserSpaces }))),
  projectModules: lazy(() => import("./ProjectModules")),
  formFlows: lazy(() => import("@Features/projects").then((module) => ({ default: module.FormFlows }))),
  reviewFlows: lazy(() => import("@Features/projects").then((module) => ({ default: module.ReviewFlows }))),
  mailConfiguration: lazy(() => import("@Features/projects").then((module) => ({ default: module.mailConfiguration }))),
  mailVisibility: lazy(() => import("@Features/projects").then((module) => ({ default: module.mailVisibility }))),
  mailWhitelist: lazy(() => import("@Features/projects").then((module) => ({ default: module.mailWhitelist }))),
};

const PROJECT_MODULE_TAB_TYPES: string[] = [
  MODULE_TAB_TYPE.FORM_FLOWS,
  MODULE_TAB_TYPE.REVIEW_FLOWS,
  MODULE_TAB_TYPE.FORM_TEMPLATES,
  MODULE_TAB_TYPE.MAIL_CONFIGURATION,
  MODULE_TAB_TYPE.MAIL_VISIBILITY,
  MODULE_TAB_TYPE.MAIL_WHITELIST,
  MODULE_TAB_TYPE.ASSET_TYPES,
];

function ProjectSetting() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const projectId: any = searchParams.get("projectId");
  const { projectData } = useSelector((state: RootState) => state);

  const dispatch = useDispatch();
  const { getProjectSettingObject } = useSelector((state: RootState) => state);

  const [settingContent, setSettingContent] = useState(searchParams.get("tab") || "general_information");
  const [showSettingsContent, setShowSettingsContent] = useState(false);

  const init = useCallback(async () => {
    dispatch(setSidebarSelectedCell("Settings"));
    dispatch(setShowStickySidebar(true));
  }, []);

  const viewPermissionMapper: any = useMemo(
    () => ({
      resource_type: true,
      currency: true,
      spaces: _.get(getProjectSettingObject, "projectSpaces.isVisible", false),
      roles: _.get(getProjectSettingObject, "roles.isVisible", false),
      users: _.get(getProjectSettingObject, "projectUsers.isVisible", false),
      groups: _.get(getProjectSettingObject, "groups.isVisible", false),
      teams: _.get(getProjectSettingObject, "teams.isVisible", false),
      inspection_location: _.get(getProjectSettingObject, "location.isVisible", false),
      WBS: _.get(getProjectSettingObject, "WBS.isVisible", false),
      general_information: _.get(getProjectSettingObject, "projectInfo.isVisible", false),
      formTemplates: _.get(getProjectSettingObject, "formTemplate.isVisible", false),
      space: _.get(getProjectSettingObject, "projectSpaces.isVisible", false),
      assetTypes: _.get(getProjectSettingObject, "assetTypes.isVisible", false),
      projectModules: true,
      // Sub-tabs reached through the Modules page. These must be gated on their own
      // permission, not on `projectModules` (which is always true) — otherwise the
      // PermissionGate is effectively open and any user can reach them via query params.
      formFlows: true,
      reviewFlows: true,
      mailConfiguration: _.get(getProjectSettingObject, "formTemplate.isVisible", false),
      mailVisibility: _.get(getProjectSettingObject, "formTemplate.isVisible", false),
      mailWhitelist: _.get(getProjectSettingObject, "formTemplate.isVisible", false),
    }),
    [getProjectSettingObject],
  );

  const renderBreadcrumb = useCallback(() => {
    dispatch(
      setBreadCrumbData([
        {
          text: t("Projects"),
          link: "/projects",
        },
        {
          text: projectData.description,
          link: getProjectDashboardPageUrl(projectId),
          showTooltip: true,
          tooltipData: projectData.description,
        },
        {
          text: t("Settings"),
        },
      ]),
    );
  }, [projectData.description, projectId]);

  useEffect(() => {
    projectData && projectData.id && renderBreadcrumb();
  }, [projectData, renderBreadcrumb]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (getProjectSettingObject && Object.keys(getProjectSettingObject).length) {
      setShowSettingsContent(true);
    }
  }, [getProjectSettingObject]);

  const getComponentToRender = () => {
    let Component = SETTINGS_COMPONENTS[settingContent];
    if (settingContent === "spaces" && searchParams.get("spaceId")) {
      Component = SETTINGS_COMPONENTS["space"];
    } else if (settingContent === "users" && searchParams.get("userId")) {
      Component = SETTINGS_COMPONENTS["userSpaces"];
    } else if (settingContent === MODULE_SETTING_TYPE.PROJECT_MODULES && searchParams.get("settingType")) {
      const comType = searchParams.get("settingType") || "";
      if (PROJECT_MODULE_TAB_TYPES.includes(comType)) {
        Component = SETTINGS_COMPONENTS[comType];
      }
    }
    return Component;
  };

  // The component actually rendered for a Modules sub-tab is the one named by
  // `settingType`, so the permission gate must key on that effective tab — not on
  // `settingContent`, which stays "projectModules" and is always allowed.
  const getGatedSettingKey = (): string => {
    if (settingContent === MODULE_SETTING_TYPE.PROJECT_MODULES) {
      const comType = searchParams.get("settingType") || "";
      if (PROJECT_MODULE_TAB_TYPES.includes(comType)) {
        return comType;
      }
    }
    return settingContent;
  };

  const renderSettingContent = () => {
    if (!showSettingsContent || !settingContent) return null;
    const Component = getComponentToRender();
    if (!Component) return null;
    return (
      <Suspense fallback={<></>}>
        <PermissionGate allowed={viewPermissionMapper[getGatedSettingKey()]}>
          <Component projectId={projectId} />
        </PermissionGate>
      </Suspense>
    );
  };

  return (
    <div className="h-full w-full pe-6 overflow-auto">
      <div
        className="bg-white flex  overflow-auto w-full h-full"
        style={{ boxShadow: "0.25rem 0.25rem 0.9375rem 0px #415CAB26" }}
      >
        <LeftSidebar setSettingContent={setSettingContent} settingContent={settingContent} />
        <div className="w-full h-full overflow-auto">{renderSettingContent()}</div>
      </div>
    </div>
  );
}

export default ProjectSetting;
